import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  baseUnit,
  ErrorBoundary,
  HighlightsArticleList,
  PrimarySectionTitle,
  SecondarySectionTitle,
  SectionHeader,
  SectionWithTitle,
  SixSixArticleList,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { Page } from '../Page';
import {
  getMetadataDescription,
  getMetadataTitle,
  getSectionArticlesByKey,
  getSectionSubtitleByKey,
  getSectionTitleByKey,
  getShowMorePaginationTextByKey,
  getShowMorePaginationURLByKey,
  getMetadataImage,
} from '../../store/template/feedPage/feedPageBangkok/feedPageBangkok.selectors';
import { getAdSlotId } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { DefaultNavigationLayout } from '../../components/connectedComponents/DefaultNavigationLayout';

interface FeedPageBangkokProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)(state) || [];
  const feedSectionArticles = getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION)(state) || [];
  return [
    ...topSectionArticles,
    ...feedSectionArticles,
  ];
};

export const FeedPageBangkokComponent: React.FunctionComponent<FeedPageBangkokProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageBangkokContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridRowGap: `${baseUnit * 1.5}px`,
    marginTop: `${baseUnit * 1.5}px`,
    marginBottom: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
  },
});

export const FeedPageBangkokContentConnected: React.FunctionComponent = () => {
  const topSectionDisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.TOP_SECTION));
  const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION));
  const cardsSectionDisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.FEED_SECTION));
  const cardsSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION));
  const articles = useSelector(getArticlesInTemplate);
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const description = useSelector(getSectionSubtitleByKey(SECTION_NAMES.TOP_SECTION));
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);
  const style = createStyle();
  return (
    <React.Fragment>
      <MetaTags articles={articles} title={metaTitle} description={metaDescription} pageImage={pageImage} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <DefaultNavigationLayout mainWrapperConfigThemeKey="mainContent">
            <Ad id={topAdId} />
            <div className={css(style.sectionsWrapper)}>

              <SectionWithTitle>
                <SectionHeader moreButtonSvg={null} moreButtonText={null} description={description}>
                  <PrimarySectionTitle>{topSectionDisplayName}</PrimarySectionTitle>
                </SectionHeader>
                <HighlightsArticleList cardsProps={topSectionArticles} />
              </SectionWithTitle>

              <SectionWithTitle>
                <SectionHeader moreButtonSvg={null} moreButtonText={null}>
                  <SecondarySectionTitle>{cardsSectionDisplayName}</SecondarySectionTitle>
                </SectionHeader>
                <SixSixArticleList cardsProps={cardsSectionArticles} />
              </SectionWithTitle>

            </div>
            <LoadMoreFeedPostsButtonConnected />
            <Ad id={oneOnOneId} />
          </DefaultNavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};

export const LoadMoreFeedPostsButtonConnected: React.FunctionComponent = () => {
  const sectionKey = SECTION_NAMES.FEED_SECTION;
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(sectionKey));
  const text = useSelector(getShowMorePaginationTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
